import type { ChannelSlice } from '../ChannelSlice';
import type { PositionsWidgetAdapter } from '../PositionsWidgetAdapter';
import type { StickyFilterSlice } from '../StickyFilterSlice';
import { makeHeaderElements } from '../WidgetModel';
import type { WidgetHeaderElement } from '../WidgetModel';
import { COMMON_HEADER_ELEMENTS, STANDALONE_COMMON_HEADER_ELEMENTS } from '../WidgetModel/impl';
import type { WidgetTabSlice } from '../WidgetTabSlice';
import { computed, signal } from '@preact/signals-react';
import type { ReadonlySignal, Signal } from '@preact/signals-react';
import type { Instrument } from '@thinkalpha/common/schemas/instruments.js';
import { EmptyArray } from '@thinkalpha/common/util/iterators.js';
import type { ColumnTemplate } from '@thinkalpha/platform-ws-client/contracts/column-template.js';
import { t } from 'i18next';
import { optional } from 'inversify';
import type { Observable } from 'rxjs';
import type { TableStatus } from 'src/components/table-view/model';
import type { GridSettingsNavigationTarget } from 'src/features/grid-settings/models/NavigationModel';
import { ReactiveInjectable, inject, injectable } from 'src/features/ioc';
import type { InstrumentType } from 'src/features/symbol-picker/state';
import { instrumentToString, parseInstrument } from 'src/lib/instrumentUtils';
import type { PositionsWidgetModel } from 'src/models/PositionsWidgetModel';
import type { TableConstructionProgress } from 'src/models/TableModel';
import { generateWorkableColumnTemplateGridSettings } from 'src/routes/dashboard/widget-container/widget-settings-editor/context';
import type { WorkspaceService } from 'src/services/WorkspaceService';
import type { PositionsWidgetViewModel } from 'src/store/types';
import type { ConnectionStatusSignal } from 'src/types/EventBus';
import { EventBusBuilder, MergedEventBus } from 'src/types/EventBus/EventBus';

@injectable()
export class PositionsWidgetModelImpl extends ReactiveInjectable implements PositionsWidgetModel {
    #channelSlice: ChannelSlice;
    #widgetTabSlice: WidgetTabSlice;

    #instrument = signal<Instrument>(parseInstrument(''));
    #instrumentString = signal<string>('');
    #allowedInstrumentTypes: ReadonlySignal<InstrumentType[]> = signal(['equity', 'options']);
    #accountIds: Signal<string[]> = signal([]);
    #platforms: Signal<string[]> = signal([]);
    #isAccountSelectionValid: Signal<boolean> = signal(true);
    #isGridSettingsDialogOpen: Signal<boolean> = signal(false);
    #gridSettingsDialogTarget: Signal<GridSettingsNavigationTarget | null> = signal(null);
    #isColumnTemplateLibraryOpen: Signal<boolean> = signal(false);
    #hideZeroPositions: Signal<boolean> = signal(false);
    #tableRowCount: Signal<number> = signal(0);
    #tableRowCountObservable: Signal<Observable<number> | undefined> = signal(undefined);
    #tableTotalRowCount: Signal<number | null> = signal(null);
    #connectionStatus: Signal<
        ConnectionStatusSignal['widget::connectionStatus'] extends ReadonlySignal<infer T> ? T : never
    > = signal({
        status: 'uninitialized' as TableStatus,
        failureCount: 0,
        progress: undefined,
    });
    #selectedColumnTemplate: Signal<ColumnTemplate | string | null> = signal(null);
    #refetchCallback: (() => void) | null = null;

    headerElements!: WidgetHeaderElement[];

    eventBus!: PositionsWidgetModel['eventBus'];

    #stickyFilterSlice: StickyFilterSlice;

    constructor(
        @inject('PositionsWidgetAdapter') private adapter: PositionsWidgetAdapter,
        @inject('ChannelSlice') channelSlice: ChannelSlice,
        @inject('WidgetTabSlice') widgetTabSlice: WidgetTabSlice,
        @inject('StickyFilterSlice') stickyFilterSlice: StickyFilterSlice,
        @inject('WorkspaceService') @optional() workspaceService?: WorkspaceService,
        @inject('TabId') @optional() tabId?: string,
    ) {
        // eslint-disable-next-line prefer-rest-params -- forwarding constructor args
        super(...arguments);
        this.#channelSlice = channelSlice;
        this.#widgetTabSlice = widgetTabSlice;
        this.#stickyFilterSlice = stickyFilterSlice;

        if (tabId && workspaceService) {
            workspaceService.registerWidget(tabId, this);
            this.disposableStack.defer(() => workspaceService.deregisterWidget(tabId));
        }

        this.eventBus = this.#buildEventBus();
    }

    get instrument(): ReadonlySignal<Instrument> {
        return this.#instrument as ReadonlySignal<Instrument>;
    }

    setInstrument(instrument: Instrument): void {
        this.adapter.persist({ instrument });
        this.#instrument.value = instrument;
        this.#instrumentString.value = instrumentToString(instrument);
    }

    update(updates: Partial<PositionsWidgetViewModel & { quantity?: number; tickerFilter?: string }>) {
        // Delegate individual known fields to adapter and update signals
        if (updates.accountIds !== undefined) {
            const accountIds = updates.accountIds ?? [];
            this.adapter.persist({ accountIds });
            this.#accountIds.value = accountIds;
        }
        if (updates.platforms !== undefined) {
            const platforms = updates.platforms ?? [];
            this.adapter.persist({ platforms });
            this.#platforms.value = platforms;
        }
        if (updates.columnTemplate !== undefined) {
            const columnTemplate = updates.columnTemplate ?? null;
            this.adapter.persist({ columnTemplate });
            this.#selectedColumnTemplate.value = columnTemplate;
        }
        if (updates.requireOpenShares !== undefined) {
            this.adapter.persist({ hideZeroPositions: updates.requireOpenShares });
            this.#hideZeroPositions.value = updates.requireOpenShares;
        }
    }

    selectPosition(symbol?: string, accountId?: string, quantity?: number) {
        this.adapter.selectPosition(symbol, accountId, quantity);
    }

    #buildEventBus(): PositionsWidgetModel['eventBus'] {
        return MergedEventBus.from(
            EventBusBuilder.create()
                .addEvent('widget::instrumentChange', (payload) => {
                    const instrument = parseInstrument(payload.instrument);
                    this.adapter.persist({ instrument });
                    this.#instrument.value = instrument;
                    this.#instrumentString.value = instrumentToString(instrument);
                })
                // Events
                .addEvent('widget::updateSelectedAccountIds', (payload) => {
                    this.adapter.persist({ accountIds: payload.accountIds });
                    this.#accountIds.value = payload.accountIds;
                })
                .addEvent('widget::updateSelectedAccountId', (payload) => {
                    this.adapter.persist({ accountIds: [payload.accountId] });
                    this.#accountIds.value = [payload.accountId];
                })
                .addEvent('widget::setIsAccountSelectionValid', (payload) => {
                    this.#isAccountSelectionValid.value = payload.isValid;
                })
                .addEvent('widget::updateSelectedPlatforms', (payload) => {
                    this.adapter.persist({ platforms: payload.platformIds });
                    this.#platforms.value = payload.platformIds;
                })
                .addEvent('widget::updateConcreteTemplate', (payload) => {
                    this.adapter.persist({ columnTemplate: payload.columnTemplate });
                    this.#selectedColumnTemplate.value = payload.columnTemplate;
                })
                .addEvent('widget::selectColumnTemplate', (payload) => {
                    this.adapter.persist({ columnTemplate: payload.templateId });
                    this.#selectedColumnTemplate.value = payload.templateId;
                })
                .addEvent('widget::onColumnTemplateLibraryOpen', (payload) => {
                    this.#isColumnTemplateLibraryOpen.value = payload.isOpen;
                })
                .addEvent('widget::toggleHideZeroPositions', (payload) => {
                    this.adapter.persist({ hideZeroPositions: payload.hideZeroPositions });
                    this.#hideZeroPositions.value = payload.hideZeroPositions;
                })
                // Signals
                .addSignal('widget::instrument', () => this.#instrumentString)
                .addSignal('widget::allowedInstrumentTypes', () => this.#allowedInstrumentTypes)
                .addSignal('widget::selectedAccountIds', () =>
                    computed(() => this.#accountIds.value ?? (EmptyArray as string[])),
                )
                .addSignal('widget::isAccountSelectionValid', () => this.#isAccountSelectionValid)
                .addSignal('widget::selectedPlatforms', () =>
                    computed(() => this.#platforms.value ?? (EmptyArray as string[])),
                )
                .addSignal('widget::connectionStatus', () => this.#connectionStatus)
                .addEvent('widget::openGridSettingsDialog', (payload) => {
                    this.#isGridSettingsDialogOpen.value = payload.isOpen;
                    this.#gridSettingsDialogTarget.value =
                        payload.isOpen && (payload.targetTab !== undefined || payload.targetColumnId !== undefined)
                            ? { tab: payload.targetTab, columnId: payload.targetColumnId }
                            : null;
                })
                .addSignal('widget::isGridSettingsDialogOpen', () => this.#isGridSettingsDialogOpen)
                .addSignal('widget::gridSettingsDialogTarget', () => this.#gridSettingsDialogTarget)
                .addSignal('widget::formattableGridSettings', () =>
                    computed(() => {
                        const columnTemplate = this.#selectedColumnTemplate.value;
                        if (!columnTemplate || typeof columnTemplate === 'string') {
                            return { settings: null, defaults: null };
                        }
                        const sanitized = generateWorkableColumnTemplateGridSettings(columnTemplate);
                        return { settings: sanitized, defaults: sanitized };
                    }),
                )
                .addSignal('widget::selectedColumnTemplate', () => this.#selectedColumnTemplate)
                .addSignal('widget::isColumnTemplateLibraryOpen', () => this.#isColumnTemplateLibraryOpen)
                .addSignal('widget::hideZeroPositions', () => this.#hideZeroPositions)
                .addSignal('widget::tableRowCount', () => this.#tableRowCount)
                .addSignal('widget::tableRowCountObservable', () => this.#tableRowCountObservable)
                .addSignal('widget::tableTotalRowCount', () => this.#tableTotalRowCount)
                .addEvent('widget::connectionStatusRefetch', () => {
                    this.#refetchCallback?.();
                })
                .build(),
        )
            .merge(this.#channelSlice.eventBus)
            .merge(this.#widgetTabSlice.eventBus)
            .merge(this.#stickyFilterSlice.eventBus);
    }

    init(tabId?: string) {
        // Always initialize slices — use a generated UUID for standalone mode so signals are available
        const effectiveTabId = tabId ?? crypto.randomUUID();
        this.#channelSlice.init(effectiveTabId);
        this.#widgetTabSlice.init(effectiveTabId);
        this.disposableStack.use(this.#channelSlice);
        this.disposableStack.use(this.#widgetTabSlice);

        if (!tabId) {
            (this.#widgetTabSlice.eventBus.getSignal('widget::name') as Signal<string>).value =
                t('widgets::positions::name');
        }

        // Initialize StickyFilterSlice with the shared adapter
        this.#stickyFilterSlice.init(this.adapter);
        this.disposableStack.use(this.#stickyFilterSlice);

        // Sync current adapter state into signals
        this.#syncFromAdapter();

        this.headerElements = createPositionsWidgetHeaderElements({ isStandalone: !tabId });
    }

    #syncFromAdapter() {
        const state = this.adapter.getState();
        this.#instrument.value = state.instrument;
        this.#instrumentString.value = instrumentToString(state.instrument);
        this.#accountIds.value = state.accountIds;
        this.#platforms.value = state.platforms;
        this.#selectedColumnTemplate.value = state.columnTemplate;
        this.#hideZeroPositions.value = state.hideZeroPositions;
    }

    setTableRowCount(count: number) {
        this.#tableRowCount.value = count;
    }

    setTableRowCountObservable(observable: Observable<number> | undefined) {
        this.#tableRowCountObservable.value = observable;
    }

    setTableTotalRowCount(count: number | null) {
        this.#tableTotalRowCount.value = count;
    }

    updateTableConnectionStatus(connectionStatus: {
        status: TableStatus;
        progress: TableConstructionProgress;
        failureCount: number;
    }): void {
        this.#connectionStatus.value = connectionStatus;
    }

    setRefetchCallback(fn: (() => void) | null): void {
        this.#refetchCallback = fn;
    }
}

export function createPositionsWidgetHeaderElements({ isStandalone = false }: { isStandalone?: boolean } = {}) {
    const baseElements = isStandalone ? STANDALONE_COMMON_HEADER_ELEMENTS : COMMON_HEADER_ELEMENTS;

    return makeHeaderElements<PositionsWidgetModel['eventBus']>([
        ...baseElements,
        // Left side (before spacer at position 50)
        { type: 'stickyFilter', position: 25, priority: 80 },
        { type: 'hideZeroPositions', position: 27, priority: 50 },
        // Right side (after spacer at position 50)
        { type: 'accounts', priority: 70, position: 60, selectType: 'multi', allAccountsLabel: 'all-accounts' },
        { type: 'platforms', position: 65, priority: 60 },
        { type: 'columnTemplateMenu', position: 70, priority: 40, columnTemplateKey: 'Position' },
        { type: 'tableRowCount', position: 75, priority: 20 },
        {
            type: 'connectionStatus',
            position: 76,
            priority: 10,
            overflowPosition: 5,
        },
    ]);
}
